import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { listCustomerAppointments, listServiceProducts } from '../../../appointments/infra/appointmentsRepo';
import { getCustomerSalesSummary, type CustomerSalesSummary } from '../../infra/customerRepository';
import type {
  Appointment,
  ServiceProductOption,
} from '../../../appointments/domain/types';

export type CustomerAppointmentRow = {
  id: string;
  serviceName: string;
  startsAt: string;
  endsAt: string;
  status: Appointment['status'];
  branchId: string;
  notes: string | null;
  resourceName: string | null;
  paymentMode: string | null;
  paymentStatus: string | null;
  saleId: string | null;
};

export type CustomerAppointmentSummary = {
  totalVisits: number;
  lastVisitAt: string | null;
  nextAppointmentAt: string | null;
  cancelledCount: number;
  noShowCount: number;
  daysSinceLastVisit: number | null;
  avgDaysBetweenVisits: number | null;
};

export type CustomerSalesWindow = {
  totalSpent: number;
  salesCount: number;
};

export type CustomerSalesWindows = {
  last30: CustomerSalesWindow;
  last90: CustomerSalesWindow;
  last365: CustomerSalesWindow;
};

const APPT_PAGE_SIZE = 5;

function mapAppointment(a: Appointment, serviceNameById: Map<string, string>): CustomerAppointmentRow {
  return {
    id: a.id,
    serviceName: serviceNameById.get(a.productId) ?? 'Servicio',
    startsAt: a.startsAt,
    endsAt: a.endsAt,
    status: a.status,
    branchId: a.branchId,
    notes: a.notes,
    resourceName: a.resourceName ?? null,
    paymentMode: a.paymentMode ?? null,
    paymentStatus: a.paymentStatus ?? null,
    saleId: a.saleId ?? null,
  };
}

export function useCustomerAppointments(
  customerId: string | null,
  visible: boolean,
) {
  const { entitlements } = usePlanEntitlements();
  const orgId = entitlements?.organization_id;
  const [appointments, setAppointments] = useState<CustomerAppointmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [salesSummary, setSalesSummary] = useState<CustomerSalesSummary | null>(null);
  const [salesWindows, setSalesWindows] = useState<CustomerSalesWindows | null>(null);
  const [apptPage, setApptPage] = useState(0);
  const [apptHasMore, setApptHasMore] = useState(false);
  const serviceCacheRef = useRef<Map<string, string> | null>(null);

  useEffect(() => {
    if (!visible || !customerId) {
      setAppointments([]);
      setSalesSummary(null);
      setSalesWindows(null);
      setApptPage(0);
      setApptHasMore(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setApptPage(0);

    const load = async () => {
      try {
        const now = new Date();
        const from30 = new Date(now.getTime() - 30 * 86_400_000).toISOString();
        const from90 = new Date(now.getTime() - 90 * 86_400_000).toISOString();
        const from365 = new Date(now.getTime() - 365 * 86_400_000).toISOString();

        const [appts, services, sales, w30, w90, w365] = await Promise.all([
          listCustomerAppointments(customerId, APPT_PAGE_SIZE, 0),
          serviceCacheRef.current
            ? Promise.resolve(serviceCacheRef.current)
            : orgId
              ? listServiceProducts(orgId).then((rows) => {
                  const map = new Map<string, string>();
                  for (const s of rows) map.set(s.id, s.name);
                  serviceCacheRef.current = map;
                  return map;
                })
              : Promise.resolve(new Map<string, string>()),
          getCustomerSalesSummary(customerId),
          getCustomerSalesSummary(customerId, from30),
          getCustomerSalesSummary(customerId, from90),
          getCustomerSalesSummary(customerId, from365),
        ]);

        if (cancelled) return;

        const serviceNameById = services as Map<string, string>;
        const rows = appts.map((a) => mapAppointment(a, serviceNameById));
        setAppointments(rows);
        setApptHasMore(rows.length === APPT_PAGE_SIZE);
        setSalesSummary(sales);
        setSalesWindows({
          last30: { totalSpent: w30?.totalSpent ?? 0, salesCount: w30?.salesCount ?? 0 },
          last90: { totalSpent: w90?.totalSpent ?? 0, salesCount: w90?.salesCount ?? 0 },
          last365: { totalSpent: w365?.totalSpent ?? 0, salesCount: w365?.salesCount ?? 0 },
        });
      } catch (err) {
        console.warn('[useCustomerAppointments] Error loading appointments', err);
        if (!cancelled) {
          setAppointments([]);
          setSalesSummary(null);
          setSalesWindows(null);
          setApptHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [customerId, visible, orgId]);

  const loadApptPage = useCallback(
    async (newPage: number) => {
      if (!customerId) return;
      setLoading(true);
      try {
        const services = serviceCacheRef.current ?? new Map<string, string>();
        const appts = await listCustomerAppointments(customerId, APPT_PAGE_SIZE, newPage * APPT_PAGE_SIZE);
        const rows = appts.map((a) => mapAppointment(a, services));
        setAppointments(rows);
        setApptPage(newPage);
        setApptHasMore(rows.length === APPT_PAGE_SIZE);
      } catch (err) {
        console.warn('[useCustomerAppointments] Error loading page', err);
      } finally {
        setLoading(false);
      }
    },
    [customerId],
  );

  const summary = useMemo<CustomerAppointmentSummary>(() => {
    if (appointments.length === 0) {
      return {
        totalVisits: 0,
        lastVisitAt: null,
        nextAppointmentAt: null,
        cancelledCount: 0,
        noShowCount: 0,
        daysSinceLastVisit: null,
        avgDaysBetweenVisits: null,
      };
    }

    const now = Date.now();
    let lastVisit: string | null = null;
    let nextAppt: string | null = null;
    let visits = 0;
    let cancelled = 0;
    let noShow = 0;

    for (const a of appointments) {
      const ts = new Date(a.startsAt).getTime();

      if (a.status === 'completed') {
        visits++;
        if (lastVisit === null || ts > new Date(lastVisit).getTime()) {
          lastVisit = a.startsAt;
        }
      } else if (a.status === 'cancelled') {
        cancelled++;
      } else if (a.status === 'no_show') {
        noShow++;
      } else if ((a.status === 'confirmed' || a.status === 'requested') && ts >= now) {
        if (nextAppt === null || ts < new Date(nextAppt).getTime()) {
          nextAppt = a.startsAt;
        }
      }
    }

    const daysSinceLastVisit = lastVisit
      ? Math.floor((now - new Date(lastVisit).getTime()) / 86_400_000)
      : null;

    let avgDaysBetweenVisits: number | null = null;
    if (visits >= 2) {
      const completedDates = appointments
        .filter((a) => a.status === 'completed')
        .map((a) => new Date(a.startsAt).getTime())
        .sort((a, b) => a - b);
      if (completedDates.length >= 2) {
        let totalDays = 0;
        for (let i = 1; i < completedDates.length; i++) {
          totalDays += (completedDates[i]! - completedDates[i - 1]!) / 86_400_000;
        }
        avgDaysBetweenVisits = Math.round(totalDays / (completedDates.length - 1));
      }
    }

    return {
      totalVisits: visits,
      lastVisitAt: lastVisit,
      nextAppointmentAt: nextAppt,
      cancelledCount: cancelled,
      noShowCount: noShow,
      daysSinceLastVisit,
      avgDaysBetweenVisits,
    };
  }, [appointments]);

  return {
    appointments,
    loading,
    summary,
    salesSummary,
    salesWindows,
    apptPage,
    apptHasMore,
    apptPageSize: APPT_PAGE_SIZE,
    loadApptPage,
  };
}
